import EditSvg from "@/src/assets/icons/icon-edit-pencel.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import { useAppSelector } from "@/src/store";
import { companyApi } from "@/src/store/apis/corporate/company";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import BankDetailEditDialog from "../dialogs/BankDetailEditDialog";
import { FlexCenter } from "./BasicInformation";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";

enum DialogState {
  NULL,
  BANK_DETAIL,
}

interface BankDetailsProps {
  role: "EXPORTER" | "IMPORTER";
}

const BankDetails: React.FC<BankDetailsProps> = ({ role }) => {
  const { t } = useTranslation();
  const i18nPrefix = "companyManagement:exporter";
  const user = useAppSelector((state) => state.auth.user);

  const {
    address,
    bankName,
    bankTel,
    bankFax,
    swiftCode,
    accountNumber,
    accountName,
    isFetching,
  } = companyApi.endpoints.getExporters.useQueryState(undefined, {
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isErrorAndUndefined = isError || currentData === undefined;

      const mergedAddress = !isErrorAndUndefined
        ? [
            currentData?.exporterDetailQueryResultDto.bankPostalCode,
            currentData?.exporterDetailQueryResultDto.bankStreetAddress,
            currentData?.exporterDetailQueryResultDto.bankLocality,
            currentData?.exporterDetailQueryResultDto.bankRegion,
            currentData?.exporterDetailQueryResultDto.bankCountryName,
          ].filter(Boolean)
        : ["-"];

      return {
        isFetching,
        address: mergedAddress.length >= 1 ? mergedAddress.join(", ") : "-",
        bankName: !isErrorAndUndefined
          ? (currentData.exporterDetailQueryResultDto.bankName ?? "-")
          : "-",
        bankTel:
          !isErrorAndUndefined &&
          currentData.exporterDetailQueryResultDto.bankTelPrefix &&
          currentData.exporterDetailQueryResultDto.bankTel
            ? `${currentData?.exporterDetailQueryResultDto.bankTelPrefix} ${currentData?.exporterDetailQueryResultDto.bankTel}`
            : "-",
        bankFax:
          !isErrorAndUndefined &&
          currentData?.exporterDetailQueryResultDto.bankFaxPrefix &&
          currentData?.exporterDetailQueryResultDto.bankFax
            ? `${currentData.exporterDetailQueryResultDto.bankFaxPrefix} ${currentData.exporterDetailQueryResultDto.bankFax}`
            : "-",
        swiftCode: !isErrorAndUndefined
          ? (currentData.exporterDetailQueryResultDto.swiftCode ?? "-")
          : "-",
        accountNumber: !isErrorAndUndefined
          ? (currentData.exporterDetailQueryResultDto.accountNumber ?? "-")
          : "-",
        accountName: !isErrorAndUndefined
          ? (currentData.exporterDetailQueryResultDto.accountName ?? "-")
          : "-",
      };
    },
  });

  const [bankDialogState, setBankDialogState] = useState(DialogState.NULL);

  const renderBankDialog = () => {
    if (bankDialogState === DialogState.NULL) {
      return;
    }

    if (bankDialogState === DialogState.BANK_DETAIL) {
      return (
        <BankDetailEditDialog
          role={role}
          open={bankDialogState === DialogState.BANK_DETAIL}
          onOpenChange={() => setBankDialogState(DialogState.NULL)}
        />
      );
    }
  };

  return (
    <SectionCard
      cardTitle={t(`${i18nPrefix}.companyInfo.bankDetail.title`)}
      rightAccessory={
        user?.exporterUserType === "CORPORATE_MANAGER" && (
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            buttonSize={32}
            onClick={() => setBankDialogState(DialogState.BANK_DETAIL)}
            disabled={isFetching}
            isLoading={isFetching}
          >
            <FlexCenter>
              <Icon iconSrc={EditSvg} iconSize={16} />
              {t(`${i18nPrefix}.companyInfo.bankDetail.editButton`)}
            </FlexCenter>
          </Button>
        )
      }
    >
      {renderBankDialog()}

      <BankDetailContainer>
        <SectionCardRow
          label={t(`${i18nPrefix}.companyInfo.bankDetail.bankName`)}
          direction="horizontal"
          value={
            <BreakWordTypo typoType="b7r" color="gray5">
              {bankName ?? "-"}
            </BreakWordTypo>
          }
        />
        <SectionCardRow
          label={t(`${i18nPrefix}.companyInfo.bankDetail.bankAddress`)}
          direction="horizontal"
          value={
            <BreakWordTypo typoType="b7r" color="gray5">
              {address}
            </BreakWordTypo>
          }
        />
        <SectionCardRow
          label={t(`${i18nPrefix}.companyInfo.bankDetail.bankTel`)}
          direction="horizontal"
          value={
            <BreakWordTypo typoType="b7r" color="gray5">
              {bankTel}
            </BreakWordTypo>
          }
        />
        <SectionCardRow
          label={t(`${i18nPrefix}.companyInfo.bankDetail.bankFax`)}
          direction="horizontal"
          value={
            <BreakWordTypo typoType="b7r" color="gray5">
              {bankFax}
            </BreakWordTypo>
          }
        />
        <SectionCardRow
          label={t(`${i18nPrefix}.companyInfo.bankDetail.swiftCode`)}
          direction="horizontal"
          value={
            <BreakWordTypo typoType="b7r" color="gray5">
              {swiftCode}
            </BreakWordTypo>
          }
        />
        <SectionCardRow
          label={t(`${i18nPrefix}.companyInfo.bankDetail.accountNumber`)}
          direction="horizontal"
          value={
            <BreakWordTypo typoType="b7r" color="gray5">
              {accountNumber}
            </BreakWordTypo>
          }
        />
        <SectionCardRow
          label={t(`${i18nPrefix}.companyInfo.bankDetail.accountName`)}
          direction="horizontal"
          value={
            <BreakWordTypo typoType="b7r" color="gray5">
              {accountName}
            </BreakWordTypo>
          }
        />
      </BankDetailContainer>
    </SectionCard>
  );
};

export default BankDetails;

const BankDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
